import { FC, useEffect, useState } from 'react';

import useDC from '@eneco/dc/useDC';
import { FlowError, Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { RadioGroup } from '@eneco/sparky';
import { Products_Offers_V3_OfferProductResponseModel } from '@monorepo-types/dc';
import { usePlaceholderContent } from '@sitecore/common';
import { Fields, ServiceGemakFlowOfferStepRendering } from '@sitecore/types/ServiceGemakFlowOfferStep';
import { useMachine } from '@xstate/react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Footer from '../../components/Footer/Footer';
import { ProductCard } from '../../components/ProductCard/ProductCard';
import { FlowContextServiceGemak } from '../../context';
import { getNonCommodityOffer, getNonCommodityOffers } from '../../helpers/dc.nonCommodity';
import { useFlowTracking } from '../../hooks/useFlowTracking';
import { offerMachineNonCommodity } from '../../machines/offerMachine.nonCommodity';
import { DC_Domain_Models_Product_MPCs } from '../../types/sgTypes';

interface FormFields {
  sgProduct?: DC_Domain_Models_Product_MPCs;
}

const OfferProductCard: FC<{
  product: Products_Offers_V3_OfferProductResponseModel;
  textualData?: Fields;
}> = ({ product, textualData }) => {
  if (!product) return;
  return <ProductCard productData={product} textualData={textualData} key={product?.marketingProductCode} />;
};

export const ServiceGemakOfferStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextServiceGemak>();
  const sendToFlowMachine = useFlowActorRef().send;

  const flowContext = useFlowSelector(state => state.context);

  const [offerState, sendToOfferMachine] = useMachine(offerMachineNonCommodity);

  const offer = offerState?.context?.offer;
  const { ServiceGemakFlowOfferStep: { fields: textualData } = {} } = usePlaceholderContent<{
    ServiceGemakFlowOfferStep: ServiceGemakFlowOfferStepRendering;
  }>();

  const availableOffers = getNonCommodityOffers('servicegemak', offer);
  const formSchema = yup.object({
    sgProduct: yup.string().required(),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    resolver: typedYupResolver<FormFields>(formSchema),
    defaultValues: {
      sgProduct: flowContext?.sgProduct as DC_Domain_Models_Product_MPCs,
    },
  });

  const { businessUnit, label } = useDC();
  const { trackViewItem, trackAddToCart, trackBeginCheckout } = useFlowTracking();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotAvailable, setIsNotAvailable] = useState(false);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);

  const selectedSgProduct = watch('sgProduct');
  useEffect(() => {
    sendToOfferMachine({
      type: 'GET_OFFER',
      config: {
        businessUnit,
        label,
      },
      values: { ...flowContext },
      offerType: 'ServiceGemak',
    });
  }, [businessUnit, flowContext, label, sendToOfferMachine]);

  useEffect(() => {
    if (selectedSgProduct) {
      const selectedOffer = getNonCommodityOffer(flowContext, selectedSgProduct, offer);

      selectedOffer && trackViewItem(selectedOffer, offer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSgProduct]);

  useEffect(() => {
    if (isSubmitSuccessful && selectedSgProduct) {
      const selectedOffer = getNonCommodityOffer(flowContext, selectedSgProduct, offer);

      selectedOffer && trackAddToCart(selectedOffer, offer);
      selectedOffer && trackBeginCheckout(selectedOffer, offer);

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...getValues(),
          offer,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('FETCHING'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
      setIsNotAuthenticated(offerState.matches('ERROR_NOT_AUTHENTICATED'));
    }
  }, [offerState]);

  return (
    <Layout.Content variant="C" isLoading={isLoading} handleSubmit={handleSubmit}>
      <>
        {isSuccessful && (
          <>
            <Header align="center">
              <Header.Title>{textualData?.content?.title?.value}</Header.Title>
            </Header>
            <Controller
              control={control}
              name="sgProduct"
              render={({ field: { onChange, value, name } }) => (
                <RadioGroup
                  aria-labelledby="sgProduct"
                  name={name}
                  direction={{ initial: 'column', lg: 'row' }}
                  alignY={{ initial: 'justify', lg: 'end' }}
                  wrap={false}
                  value={value}
                  onValueChange={onChange}
                  error={errors['sgProduct']?.message}>
                  {[...availableOffers]
                    .sort((p1, p2) => {
                      if (flowContext.sgDefault === p1.marketingProductCode) return -1;
                      if (flowContext.sgDefault === p2.marketingProductCode) return 1;
                      return 0;
                    })
                    .map(product => (
                      <OfferProductCard key={product?.marketingProductCode} product={product} textualData={textualData} />
                    ))}
                </RadioGroup>
              )}
            />
            <NextButton isLoading={isSubmitSuccessful} alignX={{ initial: 'justify', md: 'center' }}>
              {textualData?.content?.nextStepText?.value}
            </NextButton>
            <Footer />
          </>
        )}

        {isFailed && (
          <FlowError
            type={isNotAuthenticated ? 'AUTHENTIFICATION' : 'GENERIC'}
            alignY="center"
            textualData={{
              notification: isNotAvailable
                ? textualData?.notifications.productsNotAvailableNotification
                : textualData?.notifications.productsApiErrorNotification,
              buttonLabel: textualData?.notifications.tryAgainButtonText,
            }}
          />
        )}
      </>
    </Layout.Content>
  );
};
